/**
 * viewContext.ts
 * -------------------------------------------------------
 * 职责：根据当前钻取状态构建 MapViewContext，
 *       决定"地图显示哪张、相机在哪、散点展示哪些园区"。
 * -------------------------------------------------------
 */

import type { Park } from '../../../types';
import {
    CHINA_OVERVIEW_DISTANCE,
    MAINLAND_LNGLAT,
} from '../mapConstants';
import {
    parksInProvince,
    provinceAdcodeToGeoName,
} from '../provinceAdcode';
import { distanceFromProvinceSpan } from './geoHelpers';
import type { DrillState, MapLayoutMode, MapViewContext } from '../types';

/* -------- 全国视图 -------- */

interface ChinaOverviewOpts {
    drillLoading?: boolean;
    highlightProvinceGeoName?: string | null;
}

export function chinaOverviewContext(
    scatterParks: Park[],
    opts: ChinaOverviewOpts = {},
): MapViewContext {
    return {
        mapName: 'china',
        targetLngLat: MAINLAND_LNGLAT,
        viewDistance: CHINA_OVERVIEW_DISTANCE,
        viewAlpha: 89,
        viewBeta: 0,
        scatterParks,
        drillLoading: opts.drillLoading ?? false,
        highlightProvinceGeoName: opts.highlightProvinceGeoName ?? null,
        highlightCityGeoName: null,
        geoAspectRatio: null,
    };
}

/* -------- 核心：综合构建 -------- */

/**
 * @param parks          所有园区
 * @param drill          下钻状态
 * @param focusAdcode    聚焦的省份 adcode
 * @param layoutMode     布局模式
 * @param drillTargetCoord  下钻时希望居中显示的目标经纬度（园区坐标），
 *                          null 则回退到省份几何中心
 */
export function buildViewContext(
    parks: Park[],
    drill: DrillState,
    focusAdcode: string | null,
    layoutMode: MapLayoutMode,
    drillTargetCoord: [number, number] | null = null,
    highlightCityGeoName: string | null = null,
): MapViewContext {
    // 1. 无聚焦省 → 全国总览
    if (focusAdcode == null) {
        return chinaOverviewContext(parks);
    }

    const highlightName = provinceAdcodeToGeoName(focusAdcode);

    // 2. 全国总览模式（可能带省高亮）
    if (layoutMode === 'nationalOverview') {
        return chinaOverviewContext(parks, { highlightProvinceGeoName: highlightName });
    }

    // 3. 省级下钻 - 已就绪
    if (drill.kind === 'ready' && drill.adcode === focusAdcode) {
        const geoAspectRatio =
            drill.lngSpan && drill.latSpan ? drill.lngSpan / drill.latSpan : null;

        // 关键改动：如果有 drillTargetCoord，优先使用它作为相机居中点
        // 否则回退到省份几何中心 drill.centroid
        const centerLng = drillTargetCoord ? drillTargetCoord[0] : drill.centroid[0];
        const centerLat = drillTargetCoord ? drillTargetCoord[1] : drill.centroid[1];

        return {
            mapName: drill.mapName,
            targetLngLat: [centerLng, centerLat, 0],
            viewDistance: distanceFromProvinceSpan(drill.spanDeg),
            viewAlpha: 89,
            viewBeta: 0,
            scatterParks: parksInProvince(parks, focusAdcode),
            drillLoading: false,
            highlightProvinceGeoName: highlightName,
            highlightCityGeoName: highlightCityGeoName,
            geoAspectRatio,
        };
    }

    // 4. 省级下钻 - 加载中 / 其他 → 展示省内散点 + loading
    return chinaOverviewContext(parksInProvince(parks, focusAdcode), {
        drillLoading: true,
        highlightProvinceGeoName: highlightName,
    });
}
